import { useState, useRef, useEffect } from 'react'
import { clsx } from 'clsx'
import type { ChartType } from '../types'

interface ChartTypeSelectorProps {
  selected: ChartType
  onChange: (type: ChartType) => void
}

const chartTypes: { value: ChartType; label: string; icon: JSX.Element }[] = [
  { 
    value: 'candlestick', 
    label: 'Candles',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="7" y="8" width="4" height="8" rx="0.5" />
        <rect x="13" y="6" width="4" height="12" rx="0.5" />
        <line x1="9" y1="4" x2="9" y2="8" />
        <line x1="9" y1="16" x2="9" y2="20" />
        <line x1="15" y1="2" x2="15" y2="6" />
        <line x1="15" y1="18" x2="15" y2="22" />
      </svg>
    )
  },
  { 
    value: 'line', 
    label: 'Line',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 18L9 12L13 16L21 6" />
      </svg>
    )
  },
  { 
    value: 'area', 
    label: 'Area',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 18L9 12L13 16L21 6V18H3Z" fill="currentColor" fillOpacity="0.2" />
        <path d="M3 18L9 12L13 16L21 6" />
      </svg>
    )
  },
  { 
    value: 'bars', 
    label: 'Bars',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M6 4V20M6 8H3M6 16H9" />
        <path d="M12 6V18M12 6H9M12 14H15" />
        <path d="M18 8V20M18 12H15M18 16H21" />
      </svg>
    )
  },
  { 
    value: 'heikinAshi', 
    label: 'Heikin-Ashi',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="6" y="8" width="4" height="8" rx="0.5" fill="currentColor" fillOpacity="0.3" />
        <rect x="14" y="6" width="4" height="12" rx="0.5" fill="currentColor" fillOpacity="0.3" />
        <line x1="8" y1="4" x2="8" y2="8" />
        <line x1="16" y1="2" x2="16" y2="6" />
      </svg>
    )
  },
]

export function ChartTypeSelector({ selected, onChange }: ChartTypeSelectorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const selectedType = chartTypes.find(t => t.value === selected)

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="btn btn-secondary flex items-center gap-2"
      >
        {selectedType?.icon}
        <span className="hidden sm:inline">{selectedType?.label}</span>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full mt-2 left-0 bg-kit-card border border-kit-border rounded-lg shadow-xl z-50 min-w-[150px] py-1">
          {chartTypes.map(({ value, label, icon }) => (
            <button
              key={value}
              onClick={() => {
                onChange(value)
                setIsOpen(false)
              }}
              className={clsx(
                'w-full px-4 py-2 flex items-center gap-3 text-left transition-colors',
                selected === value
                  ? 'bg-kit-accent/20 text-kit-accent'
                  : 'text-gray-300 hover:bg-kit-darker'
              )}
            >
              {icon}
              <span>{label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
